import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import HorizontalCarousel from '../../components/HorizontalCarousel/HorizontalCarousel';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';

import { createSelfbookHandler } from '../../utils/selfbook';
import { images } from '../../components/shared/assets';

import './PackageDetailPage.css';
import SmartNavigation from '../../components/Navigation/SmartNavigation';

const PackageDetailPage: React.FC = () => {
  const { packageId, offerId } = useParams<{ packageId?: string; offerId?: string }>();
  const navigate = useNavigate();
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  // Asset mappings - using existing images as placeholders until we get the actual Figma assets
  const figmaAssets = {
    // Main diptych image - desert landscape with vehicle
    mainImage: images.destinationsPage.properties.atacama || '/assets/packages/main-image.png',

    // Scattered carousel images
    collageImage1: images.propertyPages.restaurantFeature || '/assets/packages/collage-1.png',
    collageImage2: images.propertyPages.villas13 || '/assets/packages/collage-2.png',
    collageImage3: images.propertyPages.masterVilla || '/assets/packages/collage-3.png',

    // Full screen background - outdoor lounge area
    fullscreenImage: images.destinationsPage.properties.patagonia || '/assets/packages/fullscreen.png',

    // Package card images for "You may also like" section
    packageImage1: images.destinationsPage.properties.atacama || '/assets/packages/package-1.png',
    packageImage2: images.destinationsPage.properties.iguazu || '/assets/packages/package-2.png',
    packageImage3: images.destinationsPage.properties.mendoza || '/assets/packages/package-3.png',

    // UI Icons
    arrowIcon: images.navigation.arrowExact || '/assets/icons/arrow.svg',
    accordionIcon: '/assets/icons/accordion.svg',
    polygonIcon: '/assets/icons/polygon.svg',
  };

  // Package data with approved copy from content maps
  const getPackageData = (id: string | undefined) => {
    switch (id) {
      case 'stay-longer':
        return {
          title: 'Stay Longer',
          destinations: 'ATACAMA, IGUAZU, MENDOZA & SANTA CATARINA',
          description: 'Connect deeper with one night on us. Stay a minimum of two nights and enjoy a complimentary third night, giving you more time to explore and unwind. * Valid for stays from May to September. * Minimum of 2 nights stay. * Savings based on double occupancy.',
          terms: 'Valid for stays from May to September. Minimum of 2 nights stay. Savings based on double occupancy.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'combine-three'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
              id: 'combine-two'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 'combine-three':
        return {
          title: 'Combine Atacama, Patagonia & Iguazú',
          destinations: 'SAVE FROM USD 2.600 PER COUPLE',
          description: 'Combine all 3 Awasi Lodges over a 10-night trip to get a discount starting at USD 1.400, plus 3 complimentary nights in connection cities (Buenos Aires, Santiago or Punta Arenas). Minimum nights: 10 *Savings based on double occupancy. *Not valid between December 20th, and January 3rd.',
          terms: 'Minimum nights: 10. Savings based on double occupancy. Not valid between December 20th, and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
              id: 'combine-two'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 'combine-two':
        return {
          title: 'Combine Atacama & Patagonia',
          destinations: 'SAVE FROM USD 1.500 PER COUPLE',
          description: 'Combine Awasi Atacama and Awasi Patagonia over a 7-night trip and get a discount starting at USD 700, plus 2 complimentary nights in connection cities (Santiago or Punta Arenas). Minimum nights: 7 *Savings based on double occupancy. *Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          terms: 'Minimum nights: 7. Savings based on double occupancy. Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage2,
              id: 'combine-three'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 're-energize':
        return {
          title: 'Atacama RE-ENERGIZE wellness retreat',
          destinations: 'From 2-5 October 2025, Awasi Atacama invites you to RE-ENERGIZE',
          description: `<p>Tucked between salt flats and star-filled skies, Awasi Atacama sets the tone for a different kind of retreat, one built around presence, stillness, and the quiet clarity that emerges when everything else fades. At the center of the experience is Teca Toscano, whose work in self-knowledge draws from years of guiding people toward awareness through body-based and meditative practices. Her sessions are immersive and intuitive, shaped by the energy of each day and the natural pace of the desert. </p> <br>
<p>Over three nights, guests will take part in: Yoga sessions held in the Cordillera de la Sal. Andean astronomic exploration beneath one of the clearest skies on Earth. Immersive gong baths and a guided energetic activation set against majestic volcanoes. Intimate conversations led by Teca on clarity, transition, and alignment. Relaxation at Puritama Hot Springs, set deep in the desert canyon.</p> <br>
<p>Each guest is welcomed into a private suite, offering comfort and seclusion throughout the stay. Between sessions, there is time to rest, reflect, and enjoy our RE-ENERGIZE specially crafted menu prepared with care, all designed to support a grounded and uninterrupted experience.</p> <br>`,
          terms: 'Dates: From 2-5 October 2025. This is a curated wellness retreat with limited availability.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
              id: 'combine-two'
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage3,
              id: 'combine-three'
            }
          ]
        };
      case 'combine-adventure':
        return {
          title: 'Combine two or more of our lodges for the perfect South American Adventure.',
          destinations: 'patagonia, atacama, iguazu, mendoza & santa catarina',
          description: 'When combining our destinations, enjoy complimentary stays at our favourite hotels in connecting cities, plus benefit from the local know-how from our experts who will help create your tailor-made experience.',
          terms: 'Standard terms and conditions apply to all bookings.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
              id: 'combine-two'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 'combine-lodges':
      case 'combined':
        return {
          title: 'Combine two or more of our lodges for the perfect South American Adventure.',
          destinations: 'Patagonia, Atacama, Iguazu, Mendoza & Santa Catarina',
          description: 'When combining our destinations, enjoy complimentary stays at our favourite hotels in connecting cities, plus benefit from the local know-how from our experts who will help create your tailor-made experience.',
          terms: 'Standard terms and conditions apply to all bookings.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
              id: 'combine-two'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 'three-lodges':
        return {
          title: 'Combine Atacama, Patagonia & Iguazú',
          destinations: 'SAVE FROM USD 2.600 PER COUPLE',
          description: 'Combine all 3 Awasi Lodges over a 10-night trip to get a discount starting at USD 1.400, plus 3 complimentary nights in connection cities (Buenos Aires, Santiago or Punta Arenas). Minimum nights: 10 *Savings based on double occupancy. *Not valid between December 20th, and January 3rd.',
          terms: 'Minimum nights: 10. Savings based on double occupancy. Not valid between December 20th, and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 'atacama-patagonia':
      case 'patagonia-atacama':
        return {
          title: 'Combine Atacama & Patagonia',
          destinations: 'SAVE FROM USD 1.500 PER COUPLE',
          description: 'Combine Awasi Atacama and Awasi Patagonia over a 7-night trip and get a discount starting at USD 700, plus 2 complimentary nights in connection cities (Santiago or Punta Arenas). Minimum nights: 7 *Savings based on double occupancy. *Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          terms: 'Minimum nights: 7. Savings based on double occupancy. Valid for stays from October 2024 until April 2025 (based on 2024-2025 Season Rates). Not valid between December 20th and January 3rd.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage2,
              id: 'combine-three'
            },
            {
              title: 'Astronomical Adventure',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'astronomical-adventure'
            },
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 're-energize'
            // }
          ]
        };
      case 'wellness-immersion':
        return {
          title: 'Atacama RE-ENERGIZE wellness retreat',
          destinations: 'From 2-5 October 2025, Awasi Atacama invites you to RE-ENERGIZE',
          description: 'This edition of RE-ENERGIZE welcomes Teca Toscano, a leading figure in the field of self-knowledge, who will guide you through transformative practices set against the stillness and vastness of Atacama. It is our curated wellness retreat in the heart of the desert, designed to reconnect body, mind and soul.',
          terms: 'Dates: From 2-5 October 2025. This is a curated wellness retreat with limited availability.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'stay-longer'
            },
            {
              title: 'Combine Atacama & Patagonia',
              tag: 'patagonia',
              image: figmaAssets.packageImage2,
              id: 'combine-two'
            },
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage3,
              id: 'combine-three'
            }
          ]
        };
      case 'wellness-desert':
        return {
          title: 'Desert Wellness Retreat',
          destinations: 'ATACAMA DESERT WELLNESS EXPERIENCE',
          description: 'Immerse yourself in the transformative power of the Atacama Desert with our specialized wellness retreat. Experience mindfulness practices, therapeutic treatments, and spiritual reconnection in one of the world\'s most pristine natural environments.',
          terms: 'Wellness retreat with specialized programming. Advance booking required.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage1,
            //   id: 're-energize'
            // },
            {
              title: 'Desert Explorer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'desert-explorer'
            },
            {
              title: 'Stay Longer',
              tag: 'atacama',
              image: figmaAssets.packageImage2,
              id: 'stay-longer'
            },
            {
              title: 'Complete Atacama Experience',
              tag: 'atacama',
              image: figmaAssets.packageImage3,
              id: 'complete-atacama'
            }
          ]
        };
      case 'iguazu-experience':
        return {
          title: 'Complete Iguazú Experience',
          destinations: 'IGUAZÚ FALLS & SURROUNDING RAINFOREST',
          description: 'Discover the thundering power of Iguazú Falls and explore the surrounding Atlantic Forest. This comprehensive experience includes private guided excursions, boat rides to the base of the falls, and nature walks through pristine rainforest trails.',
          terms: 'Seasonal availability. Weather dependent activities. Advance booking recommended.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Combine Atacama, Patagonia & Iguazú',
              tag: 'iguazú',
              image: figmaAssets.packageImage1,
              id: 'combine-three'
            },
            {
              title: 'Stay Longer',
              tag: 'iguazú',
              image: figmaAssets.packageImage2,
              id: 'stay-longer'
            },
            {
              title: 'Combine Lodges',
              tag: 'combined',
              image: figmaAssets.packageImage3,
              id: 'combine-lodges'
            }
          ]
        };
      case 'desert-explorer':
        return {
          title: 'Desert Explorer Package',
          destinations: 'ATACAMA DESERT ADVENTURE',
          description: 'Venture deep into the Atacama Desert with our expert guides. Explore otherworldly landscapes, ancient archaeological sites, and witness some of the clearest night skies on Earth. Perfect for adventurous travelers seeking authentic desert experiences.',
          terms: 'Physical fitness required for some activities. Seasonal variations apply.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Astronomical Adventure',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'astronomical-adventure'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage2,
            //   id: 'cultural-heritage'
            // },
            {
              title: 'Complete Atacama',
              tag: 'atacama',
              image: figmaAssets.packageImage3,
              id: 'complete-atacama'
            }
          ]
        };
      case 'astronomical-adventure':
        return {
          title: 'Astronomical Adventure',
          destinations: 'ATACAMA STARGAZING EXPERIENCE',
          description: 'Experience the world\'s clearest night skies in the Atacama Desert. Visit professional observatories, learn from expert astronomers, and witness celestial phenomena in one of the planet\'s premier stargazing destinations.',
          terms: 'Weather dependent. Clear skies cannot be guaranteed. Observatory visits subject to availability.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Desert Explorer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'desert-explorer'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage2,
            //   id: 'cultural-heritage'
            // },
            {
              title: 'Complete Atacama',
              tag: 'atacama',
              image: figmaAssets.packageImage3,
              id: 'complete-atacama'
            }
          ]
        };
      case 'cultural-heritage':
        return {
          title: 'Cultural Heritage Experience',
          destinations: 'ATACAMA CULTURAL IMMERSION',
          description: 'Discover the rich cultural heritage of the Atacama region. Visit ancient archaeological sites, meet local artisans, learn about pre-Columbian civilizations, and experience the living traditions of indigenous communities.',
          terms: 'Cultural sites subject to preservation restrictions. Community visits arranged with respect for local customs.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Desert Explorer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'desert-explorer'
            },
            {
              title: 'Astronomical Adventure',
              tag: 'atacama',
              image: figmaAssets.packageImage2,
              id: 'astronomical-adventure'
            },
            {
              title: 'Complete Atacama',
              tag: 'atacama',
              image: figmaAssets.packageImage3,
              id: 'complete-atacama'
            }
          ]
        };
      case 'complete-atacama':
        return {
          title: 'Complete Atacama Experience',
          destinations: 'COMPREHENSIVE ATACAMA ADVENTURE',
          description: 'The ultimate Atacama experience combining desert exploration, astronomical observations, cultural immersion, and wellness activities. This comprehensive package ensures you experience all facets of this extraordinary destination.',
          terms: 'Minimum 5 nights recommended. Some activities weather dependent. Advance booking essential.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            {
              title: 'Desert Explorer',
              tag: 'atacama',
              image: figmaAssets.packageImage1,
              id: 'desert-explorer'
            },
            {
              title: 'Astronomical Adventure',
              tag: 'atacama',
              image: figmaAssets.packageImage2,
              id: 'astronomical-adventure'
            },
            // {
            //   title: 'Cultural Heritage',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage3,
            //   id: 'cultural-heritage'
            // }
          ]
        };
      case 're-energize-atacama':
        return {
          title: 'RE-ENERGIZE Atacama Wellness',
          destinations: 'ATACAMA WELLNESS & RENEWAL',
          description: 'A specialized wellness experience in the healing environment of the Atacama Desert. Combine mindfulness practices, therapeutic treatments, and spiritual reconnection with the transformative power of this unique landscape.',
          terms: 'Wellness programming with certified practitioners. Advance booking required for specialized treatments.',
          image: figmaAssets.mainImage,
          collageImages: [
            figmaAssets.collageImage1,
            figmaAssets.collageImage2,
            figmaAssets.collageImage3
          ],
          fullscreenImage: figmaAssets.fullscreenImage,
          relatedPackages: [
            // {
            //   title: 'RE-ENERGIZE wellness immersion',
            //   tag: 'atacama',
            //   image: figmaAssets.packageImage1,
            //   id: 're-energize-wellness-immersion'
            // },
            {
              title: 'Desert Wellness Retreat',
              tag: 'atacama',
              image: figmaAssets.packageImage2,
              id: 'desert-wellness-retreat'
            },
            {
              title: 'Complete Atacama',
              tag: 'atacama',
              image: figmaAssets.packageImage3,
              id: 'complete-atacama'
            }
          ]
        };
      default:
        return {
          title: 'Package Not Found',
          destinations: '',
          description: 'The requested package could not be found.',
          terms: 'Standard terms and conditions apply to all bookings.',
          image: images.galleryMain,
          collageImages: [],
          fullscreenImage: images.galleryMain,
          relatedPackages: []
        };
    }
  };

  const packageData = getPackageData(packageId || offerId);

  const handleBooking = createSelfbookHandler(navigate, '/contact/');


  return (
    <div className="package-detail-page">
      {/* Smart Navigation Bar */}
      <SmartNavigation forceVariant='dark' />

      {/* Main Carousel Container */}
      <div className="package-detail-container">
        {/* Horizontal Scrolling Carousel */}
        <HorizontalCarousel fixTop>
          {/* Section 1: Intro - Text + Image */}
          <section className="package-detail-section package-intro">
            <div className="package-intro-left">
              <div>
                <button className="package-intro-back" onClick={() => navigate('/packages')}>
                  <img src={figmaAssets.polygonIcon} alt="back" className="package-intro-back-icon" />
                  <span>back</span>
                </button>
              </div>

              <div className="package-intro-content">
                <div className="package-intro-header">
                  <h1 className="package-intro-title">{packageData.title}</h1>
                  <p className="package-intro-destinations">{packageData.destinations}</p>
                </div>

                <div className="package-intro-body">
                  <p className="package-intro-description" dangerouslySetInnerHTML={{ __html: packageData.description }}></p>

                  {/* Terms & Conditions Accordion */}
                  <div className="package-intro-accordion">
                    <button
                      className="package-intro-accordion-header"
                      onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                    >
                      <span>Terms & Conditions</span>
                      <img
                        src={figmaAssets.accordionIcon}
                        alt="open"
                        className={`package-intro-accordion-icon ${isAccordionOpen ? 'open' : ''}`}
                      />
                    </button>
                    {isAccordionOpen && (
                      <div className="package-intro-accordion-content">
                        <p>{packageData.terms}</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <button
                      className="package-intro-book-button"
                      onClick={handleBooking}
                    >
                      <span>book now</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="package-intro-right">
              <img src={packageData.image || images.destinationsPage.properties.atacama} alt="Awasi package" />
            </div>
          </section>

          {/* Section 2: Scattered Carousel - 3 Images */}
          <section className="package-detail-section package-collage">
            <div
              className="package-collage-image package-collage-image-1"
              style={{ backgroundImage: `url('${packageData.collageImages[0] || images.propertyPages.restaurantFeature}')` }}
            />
            <div
              className="package-collage-image package-collage-image-2"
              style={{ backgroundImage: `url('${packageData.collageImages[1] || images.propertyPages.villas13}')` }}
            />
            <div
              className="package-collage-image package-collage-image-3"
              style={{ backgroundImage: `url('${packageData.collageImages[2] || images.propertyPages.masterVilla}')` }}
            />
          </section>

          {/* Section 3: Full Screen Image with Overlay */}
          <section className="package-detail-section package-fullwidth"
            style={{ backgroundImage: `url('${packageData.fullscreenImage || images.destinationsPage.properties.patagonia}')` }}
          >
            <h2 className="package-fullwidth-overlay-label">
              You may also like
            </h2>
          </section>

          {/* Section 4: trip - 3 Package Cards */}
          <section className="package-detail-section package-trip">
            <div className="package-trip-cards-container" style={{ gridTemplateColumns: `repeat(${packageData.relatedPackages.length}, 1fr)` }}>
              {packageData.relatedPackages.map((pkg, index) => (
                <a key={index} className="package-trip-card" href={`/packages/${pkg.id}`}>
                  <div className="package-trip-card-image-container">
                    <div className="package-trip-card-image">
                      <img src={pkg.image || images.destinationsPage.properties.atacama} alt={pkg.title} />
                    </div>
                  </div>
                  <div className="package-trip-card-content">
                    <h3 className="package-trip-card-title">{pkg.title}</h3>
                    <span className="package-trip-card-link" >
                      view more
                      <img alt="underline" className="package-trip-card-link-underline" src="/assets/figma/8dbc3099431a81a908d397126113c040d17af44d.svg" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </section>
        </HorizontalCarousel>
      </div>

      {/* Instagram Section */}
      <InstagramSection
        posts={
          [
            { src: images.film13, alt: "Instagram post 1" },
            { src: images.filmVideo1, alt: "Instagram post 2", isVideo: true },
            { src: images.film14, alt: "Instagram post 3" },
            { src: images.film15, alt: "Instagram post 4", },
          ]
        }
      />

      {/* Footer - Atacama variant */}
      <Footer variant="atacama" />
    </div>
  );
};

export default PackageDetailPage;